import React from 'react';
import { Toggle } from '../../ui/Toggle';
import { initiativeSamples } from '../../../data/beheer/persona';
import type { InitiativeSettings } from '../../../types/settings';
interface InitiativePanelProps {
  value: InitiativeSettings;
  onChange: (v: InitiativeSettings) => void;
}
const CHANNELS: {
  id: keyof InitiativeSettings['channels'];
  label: string;
}[] = [{
  id: 'wand',
  label: 'Wandtablet'
}, {
  id: 'bureau',
  label: 'Bureau'
}, {
  id: 'telefoon',
  label: 'Telefoon'
}, {
  id: 'stem',
  label: 'Uitspreken'
}];

/** How eager Anna is to act or propose on her own — with a live preview of what passes the bar. */
export function InitiativePanel({
  value,
  onChange
}: InitiativePanelProps) {
  const pct = (v: number) => `${Math.round(v * 100)}`;
  return <div className="grid gap-8 rounded-3xl border border-white/[0.07] bg-night-900 p-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:p-8" data-id="element-84" data-mp="2pkt9y74s5yrs">
      <div className="space-y-6 text-[14px]" data-id="element-85" data-mp="2ucl1okzoaaji">
        <div data-id="element-86" data-mp="2udcp38j491yp">
          <div className="flex items-baseline justify-between" data-id="element-87" data-mp="nu4nx57frpuw">
            <label htmlFor="threshold" className="text-ink-soft" data-id="element-88" data-mp="nucxoo89if1v">Drempel initiatiefscore</label>
            <span className="font-display text-[28px] font-light tabular-nums text-ink" data-id="element-89" data-mp="iwv0fv3lxo9p">{pct(value.threshold)}</span>
          </div>
          <input id="threshold" type="range" min={0.4} max={0.95} step={0.01} value={value.threshold} onChange={e => onChange({
          ...value,
          threshold: Number(e.target.value)
        })} className="mt-2 w-full accent-[rgb(var(--accent))]" data-id="element-90" data-mp="rvq7taie14el" />
          <p className="mt-1 flex justify-between text-[12px] text-ink-muted" data-id="element-91" data-mp="1fk9fpoxizf65">
            <span data-id="element-92" data-mp="1bj60n4naxn5z">Doet en stelt veel voor</span>
            <span data-id="element-93" data-mp="1bkh0itdmmart">Alleen als het zeker is</span>
          </p>
        </div>

        <div className="flex items-center justify-between gap-3" data-id="element-94" data-mp="3hdfdmqh6o8rq">
          <label htmlFor="perHour" className="text-ink-soft" data-id="element-95" data-mp="3dbc3c5693a81">Maximaal per uur</label>
          <input id="perHour" type="number" min={0} max={12} value={value.perHour} onChange={e => onChange({
          ...value,
          perHour: Math.max(0, Math.min(12, Number(e.target.value)))
        })} className="h-9 w-20 rounded-full border border-white/10 bg-white/[0.03] px-3 text-center tabular-nums text-ink focus:border-accent/50 focus:outline-none" data-id="element-96" data-mp="1gafy1f1onh0c" />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-97" data-mp="1z8iqk3tfvw2w">
          <span className="text-ink-soft" data-id="element-98" data-mp="247lm8xnj4py7">Stille uren</span>
          <span className="flex items-center gap-2 text-ink-muted" data-id="element-99" data-mp="2460q53lkgyy8">
            <label className="sr-only" htmlFor="quietFrom" data-id="element-100" data-mp="28zlt7a20oj4a">Van</label>
            <input id="quietFrom" type="time" value={value.quietFrom} onChange={e => onChange({
            ...value,
            quietFrom: e.target.value
          })} className="h-9 rounded-full border border-white/10 bg-white/[0.03] px-3 tabular-nums text-ink focus:outline-none" data-id="element-101" data-mp="2dyg8i20rzpjn" />
            tot
            <label className="sr-only" htmlFor="quietTo" data-id="element-102" data-mp="b9ez1t27atq7">Tot</label>
            <input id="quietTo" type="time" value={value.quietTo} onChange={e => onChange({
            ...value,
            quietTo: e.target.value
          })} className="h-9 rounded-full border border-white/10 bg-white/[0.03] px-3 tabular-nums text-ink focus:outline-none" data-id="element-103" data-mp="794umku24bdg" />
          </span>
        </div>

        <fieldset data-id="element-104" data-mp="q0nxmro2zpfh">
          <legend className="text-ink-soft" data-id="element-105" data-mp="q05qgzzvrqss">Mag je bereiken via</legend>
          <div className="mt-3 grid grid-cols-2 gap-3" data-id="element-106" data-mp="l9flhnvd9pym">
            {CHANNELS.map(c => <Toggle key={c.id} label={c.label} checked={value.channels[c.id]} onChange={v => onChange({
            ...value,
            channels: {
              ...value.channels,
              [c.id]: v
            }
          })} data-id="element-107" data-mp="gam59nop3kur" />)}
          </div>
        </fieldset>
      </div>

      <div className="border-t border-white/[0.06] pt-6 lg:border-l lg:border-t-0 lg:pl-8 lg:pt-0" data-id="element-108" data-mp="thicjvmudjr3">
        <p className="text-[13px] text-ink-muted" data-id="element-109" data-mp="xilssj8sxhsu">Recente kandidaten tegen deze drempel</p>
        <ul className="mt-4 space-y-3" data-id="element-110" data-mp="jj2lttrq8ci4">
          {initiativeSamples.map(s => {
          const pass = s.score >= value.threshold;
          return <li key={s.action} data-id="element-111" data-mp="oip7mrlmf12j">
                <div className="flex items-baseline justify-between gap-4 text-[14px]" data-id="element-112" data-mp="1c7ro3w4iqnvg">
                  <span className={pass ? 'text-ink' : 'text-ink-muted'} data-id="element-113" data-mp="1ca44w5yjis6x">{s.action}</span>
                  <span className={`text-[12px] font-medium ${pass ? 'text-accent' : 'text-ink-muted'}`} data-id="element-114" data-mp="1h8xhzx9x0h6k">
                    {pass ? 'gaat door' : 'blijft stil'}
                  </span>
                </div>
                <div className="relative mt-1.5 h-1 rounded-full bg-white/[0.06]" data-id="element-115" data-mp="17eos7hc9r2c3">
                  <div className={`h-full rounded-full ${pass ? 'bg-accent' : 'bg-white/20'}`} style={{
                width: `${s.score * 100}%`
              }} data-id="element-116" data-mp="1v5axx1yswaix" />
                  <span aria-hidden className="absolute -top-1 h-3 w-px bg-ink" style={{
                left: `${value.threshold * 100}%`
              }} data-id="element-117" data-mp="3knhq587uoi71" />
                </div>
              </li>;
        })}
        </ul>
      </div>
    </div>;
}